/**
 * FirstRunTutorial — Overlay de bienvenida al primer login.
 *
 * 4 pasos guiando las interacciones clave. Se guarda un flag en localStorage
 * para no volver a mostrarlo (`cgt_first_run_seen`). El alumno puede saltarlo
 * o reabrirlo desde "Ayuda" en cualquier momento.
 */

const PANEL_ID = 'cgt-first-run';
const STYLE_ID = 'cgt-first-run-styles';
const FLAG = 'cgt_first_run_seen';

interface Step {
  title: string;
  body: string;
  icon: string;
}

const STEPS: Step[] = [
  {
    icon: '🩺',
    title: 'Bienvenido a Dr Newen IA',
    body: 'Simulador de triage clínico con hand tracking, IA y física 3D. En 4 pasos rápidos te muestro cómo interactuar.',
  },
  {
    icon: '✋',
    title: 'Mostrá las manos',
    body: 'Dr Newen te ve por la cámara. Levantá una mano frente al video: vas a ver las líneas del esqueleto dibujadas y un cursor azul siguiendo tu dedo índice.',
  },
  {
    icon: '👌',
    title: 'Pinch = click',
    body: 'Junta el pulgar con el índice sobre un nodo para seleccionarlo. Vas a ver su valor clínico (ej. "Troponina: 850 ng/L"). Con dos manos separándose o juntándose hacés zoom de la cámara.',
  },
  {
    icon: '🔎',
    title: 'Descubrí el caso',
    body: 'Al inicio solo ves anamnesis y síntomas. Ordená exámenes desde el panel que aparece y los labs/imágenes se revelan. El diagnóstico correcto aparece cuando acertás en el diferencial.',
  },
];

export class FirstRunTutorial {
  private root: HTMLElement | null = null;
  private step = 0;

  shouldShow(): boolean {
    return localStorage.getItem(FLAG) !== '1';
  }

  /** Muestra el tutorial. Si ya se vio, mostrarlo igual (para reabrir desde Ayuda). */
  show(force = false): void {
    if (!force && !this.shouldShow()) return;
    this.injectStyles();
    this.close();

    this.step = 0;
    const root = document.createElement('div');
    root.id = PANEL_ID;
    document.body.appendChild(root);
    this.root = root;
    this.render();
    requestAnimationFrame(() => root.classList.add('cgt-fr-visible'));
  }

  close(done = false): void {
    if (done) localStorage.setItem(FLAG, '1');
    if (!this.root) return;
    const el = this.root;
    el.classList.remove('cgt-fr-visible');
    setTimeout(() => el.remove(), 200);
    this.root = null;
  }

  private render(): void {
    if (!this.root) return;
    const s = STEPS[this.step];
    const isLast = this.step === STEPS.length - 1;
    this.root.innerHTML = `
      <div class="cgt-fr-backdrop"></div>
      <div class="cgt-fr-card">
        <div class="cgt-fr-progress">
          ${STEPS.map((_, i) => `
            <span class="cgt-fr-dot ${i === this.step ? 'active' : ''} ${i < this.step ? 'done' : ''}"></span>
          `).join('')}
        </div>
        <div class="cgt-fr-icon">${s.icon}</div>
        <h2>${s.title}</h2>
        <p>${s.body}</p>
        <div class="cgt-fr-actions">
          <button type="button" class="cgt-fr-skip" data-skip>${isLast ? 'Cerrar' : 'Saltar'}</button>
          ${this.step > 0 ? '<button type="button" class="cgt-fr-back" data-back>← Atrás</button>' : ''}
          <button type="button" class="cgt-fr-next" data-next>${isLast ? '¡Empezar!' : 'Siguiente →'}</button>
        </div>
      </div>
    `;
    this.root.querySelector<HTMLButtonElement>('[data-next]')?.addEventListener('click', () => {
      if (isLast) this.close(true);
      else { this.step++; this.render(); }
    });
    this.root.querySelector<HTMLButtonElement>('[data-back]')?.addEventListener('click', () => {
      if (this.step > 0) { this.step--; this.render(); }
    });
    this.root.querySelector<HTMLButtonElement>('[data-skip]')?.addEventListener('click', () => this.close(true));
  }

  private injectStyles(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      #${PANEL_ID} {
        position: fixed; inset: 0; z-index: 16000;
        font-family: -apple-system, BlinkMacSystemFont, 'Inter', sans-serif;
        color: #f5f5f7;
        opacity: 0; transition: opacity 0.25s;
      }
      #${PANEL_ID}.cgt-fr-visible { opacity: 1; }
      #${PANEL_ID} .cgt-fr-backdrop {
        position: absolute; inset: 0;
        background: radial-gradient(circle at 30% 30%, rgba(41,151,255,0.12), transparent 60%), rgba(0,0,0,0.72);
        backdrop-filter: blur(6px);
      }
      #${PANEL_ID} .cgt-fr-card {
        position: relative;
        max-width: 420px; width: 92vw;
        margin: 18vh auto 0;
        padding: 32px 28px 22px;
        background: #0a0a0a;
        border: 0.5px solid rgba(255,255,255,0.1);
        border-radius: 18px;
        box-shadow: 0 24px 72px rgba(0,0,0,0.55);
        text-align: center;
      }
      #${PANEL_ID} .cgt-fr-progress {
        display: flex; justify-content: center; gap: 6px; margin-bottom: 18px;
      }
      #${PANEL_ID} .cgt-fr-dot {
        width: 22px; height: 3px; border-radius: 2px;
        background: rgba(255,255,255,0.12);
        transition: background 0.2s;
      }
      #${PANEL_ID} .cgt-fr-dot.active { background: #2997FF; }
      #${PANEL_ID} .cgt-fr-dot.done { background: rgba(41,151,255,0.45); }
      #${PANEL_ID} .cgt-fr-icon {
        font-size: 42px; margin-bottom: 14px;
        filter: drop-shadow(0 4px 12px rgba(41,151,255,0.3));
      }
      #${PANEL_ID} h2 {
        font-size: 20px; font-weight: 700; letter-spacing: -0.4px;
        color: #f5f5f7; margin: 0 0 10px;
      }
      #${PANEL_ID} p {
        font-size: 13px; color: #c7c7cc; line-height: 1.6;
        margin: 0 0 22px;
      }
      #${PANEL_ID} .cgt-fr-actions {
        display: flex; gap: 8px; justify-content: space-between;
      }
      #${PANEL_ID} .cgt-fr-actions button {
        padding: 10px 16px; border-radius: 8px;
        border: none; cursor: pointer; font-size: 12px; font-weight: 600;
        font-family: inherit;
      }
      #${PANEL_ID} .cgt-fr-skip {
        background: transparent; color: #8e8e93;
        border: 0.5px solid rgba(255,255,255,0.08);
      }
      #${PANEL_ID} .cgt-fr-back {
        background: rgba(255,255,255,0.05); color: #a1a1a6;
      }
      #${PANEL_ID} .cgt-fr-next {
        flex: 1; margin-left: auto;
        background: linear-gradient(135deg, #2997FF, #BF5AF2);
        color: #fff;
      }
      #${PANEL_ID} .cgt-fr-next:hover { filter: brightness(1.08); }
    `;
    document.head.appendChild(style);
  }
}

export const firstRunTutorial = new FirstRunTutorial();
